import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { DevSessionBadge } from "./DevSessionBadge";
import { CoachDock } from "./coach/CoachDock";
import { armFirstRunPayoff } from "./coach/firstRunPayoff";
import { RequireFirstRun } from "./RequireFirstRun";
import { PendingDeletionGate } from "./PendingDeletionGate";
import { ConsentGate } from "./consent/ConsentGate";
import { UnitsProvider } from "../lib/UnitsProvider";
import { hasSession } from "../api/auth";
import { useAiOff } from "../api/consents";

/**
 * A SYNCHRONOUS AUTH GATE, ABOVE EVERY HOOK AND EVERY PIXEL.
 *
 * Until now the shell had none. `RequireFirstRun` guards the `<Outlet/>` only,
 * and says so in its own docstring: `Sidebar` and `CoachDock` are its siblings
 * and render regardless. Auth itself was enforced REACTIVELY, by `apiFetch`'s
 * 401 handler (`api/client.ts`) calling `redirectToLogin()`. So a signed out
 * visitor opening any URL under `/` got the full app chrome -- the rail, and a
 * coach composer inviting them to type -- for one whole API round trip before
 * being thrown to `/login`. Invisible on a local stack, plainly visible on a
 * slow connection.
 *
 * `hasSession()` reads localStorage (a Developer Mode token, or the hint that the
 * server confirmed a cookie session: the cookie is httpOnly and the page cannot
 * see it), so this costs nothing and resolves before the first paint. It is deliberately a SEPARATE component from the shell: an
 * early return inside the shell would still have run the shell's hooks (`useMe`
 * through `RequireFirstRun`, `UnitsProvider`'s profile query), firing requests
 * from someone who has no session, and rules-of-hooks would
 * have forced the gate below the hooks it exists to prevent.
 *
 * WHAT IT DOES NOT COVER, named rather than hidden: a token that exists but is
 * expired or revoked. That cannot be known without asking the server, so the
 * chrome does paint for that one round trip. Closing it too would mean holding
 * the whole shell back until `/api/me` answers, which costs every legitimate
 * cold load a blank screen to spare a rarer case. The reactive 401 still
 * catches it.
 */
export function Layout() {
  if (!hasSession()) return <Navigate to="/login" replace />;
  // An account pending deletion sees the full-screen choice instead of the shell (#365): the shell
  // is not mounted, so none of its requests (every one would answer 403) is made.
  //
  // A person who must be asked for the AI consent (#399: never asked, or the text changed since they
  // answered) meets that ask instead of the shell, inside the deletion screen's gate so a pending
  // account meets its own screen first. First run is outside the shell and asks for itself.
  return (
    <PendingDeletionGate>
      <ConsentGate>
        <AppShell />
      </ConsentGate>
    </PendingDeletionGate>
  );
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  // Without the AI consent the dock is a link to the coach page, which says why (#399, CS-D12). Read
  // here, once, so the dock itself stays a plain component.
  const aiOff = useAiOff();
  // The coach is the one page whose message list is the single scroll region.
  // This condition used to read `=== "/"` because the coach *was* the index
  // route; it follows the coach to /coach rather than disappearing.
  const onCoach = location.pathname === "/coach";

  // FirstRun.tsx completes with `navigate('/', { state: { firstRun: true }
  // })` (Task 10, FR-D6). Layout is the right place to catch it: it is
  // mounted for every route inside the shell and survives the client-side
  // navigation from "/" to "/coach", where the payoff is actually fetched
  // (see firstRunPayoff.ts's doc comment for the full chain). Armed here,
  // then the navigation state is immediately replaced with `null` --
  // `location.state` is part of the History API entry and survives a page
  // reload on its own, so leaving it in place would re-arm the payoff every
  // time "/" is reloaded, which is exactly the banner FR-D6 rejects.
  useEffect(() => {
    if (!(location.state as { firstRun?: boolean } | null)?.firstRun) return;
    armFirstRunPayoff();
    navigate(location.pathname, { replace: true, state: null });
  }, [location, navigate]);

  return (
    // Inside the app's QueryClientProvider (main.tsx) — UnitsProvider calls
    // useProfile() — and outside every routed page, so Sidebar, the Outlet,
    // CoachDock and DevSessionBadge all resolve useUnits(). Layout is the
    // shell for every route that has a profile at all; /login, /invite/:code
    // and /first-run sit outside it deliberately (router.tsx), which is
    // exactly where a bound formatter would have nothing to bind to.
    <UnitsProvider>
      <div
        className="relative min-h-screen"
        style={{
          // The warm-editorial page gradient is now painted at document level
          // (html/body in index.html), which also covers overscroll — this
          // wrapper stays transparent so it isn't a second paint layer.
          color: 'var(--text-cream)',
        }}
      >
        <Sidebar />

        {/* Two shapes. /coach is a fixed-viewport page whose message list is the
            single scroll region and whose composer sits inside it, so it takes
            no dock clearance. Every other page is a normal scrolling document
            whose bottom padding comes from --coach-dock-clearance rather than a
            Tailwind class, because the dock's own offset is defined in the same
            place and the two have to agree — the mobile value depends on
            env(safe-area-inset-bottom). */}
        <main
          className={
            onCoach
              ? "relative z-1 ml-0 flex h-dvh flex-col overflow-hidden p-4 pb-24 sm:ml-24 sm:px-8 sm:pt-6 sm:pb-0"
              : "relative z-1 ml-0 p-4 sm:ml-24 sm:p-8"
          }
          style={onCoach ? undefined : { paddingBottom: 'var(--coach-dock-clearance)' }}
        >
          <RequireFirstRun>
            <Outlet />
          </RequireFirstRun>
        </main>

        {/* The coach's global entry point. Mounted unconditionally — CoachDock
            returns null on /coach itself, so the guard lives with the component
            rather than being duplicated here. */}
        <CoachDock aiOff={aiOff} />

        <DevSessionBadge />
      </div>
    </UnitsProvider>
  );
}
